import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send, CheckCircle, Loader2 } from 'lucide-react';
import { submitContact } from '../lib/api';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, ease: 'easeOut' }
};

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    business: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');
    try {
      await submitContact(formData);
      setIsSubmitted(true);
    } catch (err) {
      const msg = err.response?.data?.error?.message || 'Something went wrong. Please try again or email us directly.';
      setSubmitError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Hero */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeInUp} className="max-w-3xl mx-auto text-center">
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-primary mb-6">
              Get in Touch
            </h1>
            <p className="text-lg text-muted-foreground">
              Tell us about your business and what you need. We'll get back to you 
              within one working day with some initial thoughts.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-8 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <motion.div {...fadeInUp} className="lg:col-span-1">
              <h2 className="font-heading text-2xl font-bold text-foreground mb-6">
                Contact Details
              </h2>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-accent/10 rounded-xl flex items-center justify-center shrink-0">
                    <Mail className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground mb-1">Email</p>
                    <a 
                      href="mailto:welcome@docksidedesign.co.uk"
                      className="text-muted-foreground hover:text-accent transition-colors"
                    >
                      welcome@docksidedesign.co.uk
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-accent/10 rounded-xl flex items-center justify-center shrink-0">
                    <Phone className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground mb-1">Phone</p>
                    <a 
                      href="tel:+441234567890" 
                      className="text-muted-foreground hover:text-accent transition-colors"
                    >
                      +44 7799378086
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-accent/10 rounded-xl flex items-center justify-center shrink-0">
                    <MapPin className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground mb-1">Service Area</p>
                    <p className="text-muted-foreground">
                      United Kingdom
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      We work with businesses across the UK
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-8 p-6 bg-muted/50 rounded-xl">
                <p className="text-sm text-muted-foreground">
                  <span className="font-semibold text-foreground">Response time:</span> We 
                  typically respond within one working day. For urgent enquiries, 
                  give us a call.
                </p>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div {...fadeInUp} className="lg:col-span-2">
              <Card className="border-border/50">
                <CardContent className="p-6 md:p-8">
                  {isSubmitted ? (
                    <div className="text-center py-12" data-testid="contact-success">
                      <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-6">
                        <CheckCircle className="w-8 h-8 text-accent" />
                      </div>
                      <h3 className="font-heading text-2xl font-bold text-foreground mb-4">
                        Thanks for Getting in Touch!
                      </h3>
                      <p className="text-muted-foreground mb-6">
                        We've received your message and will get back to you within one working day.
                      </p>
                      <Button
                        onClick={() => {
                          setIsSubmitted(false);
                          setFormData({ name: '', email: '', phone: '', business: '', message: '' });
                        }}
                        variant="outline"
                        className="rounded-full"
                      >
                        Send Another Message
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} data-testid="contact-form">
                      <h2 className="font-heading text-2xl font-bold text-foreground mb-6">
                        Tell Us About Your Project
                      </h2>
                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="name">Your Name *</Label>
                          <Input
                            id="name"
                            name="name"
                            data-testid="contact-name-input"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="John Smith"
                            required
                            className="rounded-lg"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email">Email Address *</Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            data-testid="contact-email-input"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="john@example.com"
                            required
                            className="rounded-lg"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="phone">Phone Number</Label>
                          <Input
                            id="phone"
                            name="phone"
                            type="tel"
                            data-testid="contact-phone-input"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="+44 7123 456789"
                            className="rounded-lg"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="business">Business Name</Label>
                          <Input
                            id="business"
                            name="business"
                            data-testid="contact-business-input"
                            value={formData.business}
                            onChange={handleChange}
                            placeholder="Your Business Ltd"
                            className="rounded-lg"
                          />
                        </div>
                      </div>
                      <div className="space-y-2 mt-6">
                        <Label htmlFor="message">Tell Us About Your Project *</Label>
                        <Textarea
                          id="message"
                          name="message"
                          data-testid="contact-message-input"
                          value={formData.message}
                          onChange={handleChange}
                          placeholder="What kind of website do you need? What's your business about? Any specific features you're looking for?"
                          required
                          rows={6}
                          className="rounded-lg resize-none"
                        />
                      </div>
                      {submitError && (
                        <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                          {submitError}
                        </div>
                      )}
                      <div className="mt-8">
                        <Button
                          type="submit"
                          disabled={isSubmitting}
                          data-testid="contact-submit-btn"
                          className="w-full md:w-auto bg-accent text-accent-foreground hover:bg-accent/90 rounded-full px-8 py-6 text-lg"
                        >
                          {isSubmitting ? (
                            <>Sending... <Loader2 className="ml-2 w-5 h-5 animate-spin" /></>
                          ) : (
                            <>Send Message <Send className="ml-2 w-5 h-5" /></>
                          )}
                        </Button>
                      </div>
                      <p className="text-sm text-muted-foreground mt-4">
                        * Required fields. We use your details to respond to your enquiry and will retain
                        them for up to 12 months. See our{' '}
                        <a href="/privacy" className="text-accent hover:underline">Privacy Policy</a> for
                        full details on how we handle your data.
                      </p>
                    </form>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ CTA */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeInUp}>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-6">
              Have Questions First?
            </h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Check out our pricing page for common questions, or just reach out — 
              we're happy to chat through anything.
            </p>
            <Button
              asChild
              variant="outline"
              data-testid="contact-pricing-link"
              className="rounded-full px-8 py-6 text-lg border-2"
            >
              <a href="/pricing">View Pricing & FAQ</a>
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
